import { isArray, isNil } from 'lodash-es';
import {
  ApplicationBuildActions,
  ApplicationBuildReducerState,
  CLEAN_APPLICATION_BUILD_PROPERTIES,
  CLEAN_ERROR,
  CREATE_APPLICATION_BUILD,
  CREATE_APPLICATION_BUILD_IS_ERROR,
  CREATE_APPLICATION_BUILD_IS_PENDING,
  DELETE_APPLICATION_BUILD,
  DELETE_APPLICATION_BUILD_IS_ERROR,
  DELETE_APPLICATION_BUILD_IS_PENDING,
  FETCH_APPLICATION_BUILD_BY_ID,
  FETCH_APPLICATION_BUILD_BY_ID_IS_ERROR,
  FETCH_APPLICATION_BUILD_BY_ID_IS_PENDING,
  FETCH_APPLICATION_BUILD_PROPERTIES,
  FETCH_APPLICATION_BUILD_PROPERTIES_IS_ERROR,
  FETCH_APPLICATION_BUILD_PROPERTIES_IS_PENDING,
  FETCH_APPLICATION_BUILDS,
  FETCH_APPLICATION_BUILDS_IS_ERROR,
  FETCH_APPLICATION_BUILDS_IS_PENDING,
  SORT,
  UPDATE_APPLICATION_BUILD,
  UPDATE_APPLICATION_BUILD_IS_ERROR,
  UPDATE_APPLICATION_BUILD_IS_PENDING,
  UPDATE_APPLICATION_BUILD_PROPERTIES,
  UPDATE_APPLICATION_BUILD_PROPERTIES_IS_ERROR,
  UPDATE_APPLICATION_BUILD_PROPERTIES_IS_PENDING,
} from 'services/GameHosting/ApplicationBuild/types';
import { addNew, parseError, sort } from 'utils/functions';

const INITIAL_STATE = {
  applicationBuilds: [],
  fetchApplicationBuildsIsCompleted: false,
  fetchApplicationBuildsIsPending: false,
  fetchApplicationBuildsIsError: null,

  applicationBuildById: undefined,
  fetchApplicationBuildByIdIsCompleted: false,
  fetchApplicationBuildByIdIsPending: false,
  fetchApplicationBuildByIdIsError: null,

  applicationBuildProperties: undefined,
  fetchApplicationBuildPropertiesIsCompleted: false,
  fetchApplicationBuildPropertiesIsPending: false,
  fetchApplicationBuildPropertiesIsError: null,

  sortBy: 'name',
  sortDirection: 'ASC',
  params: undefined,

  createApplicationBuildIsCompleted: false,
  createApplicationBuildIsPending: false,
  createApplicationBuildIsError: null,
  updateApplicationBuildIsCompleted: false,
  updateApplicationBuildIsPending: false,
  updateApplicationBuildIsError: null,
  updateApplicationBuildPropertiesIsCompleted: false,
  updateApplicationBuildPropertiesIsPending: false,
  updateApplicationBuildPropertiesIsError: null,
  deleteApplicationBuildIsCompleted: false,
  deleteApplicationBuildIsPending: false,
  deleteApplicationBuildIsError: null,
} satisfies ApplicationBuildReducerState;

const applicationBuildReducer = (
  previousState: ApplicationBuildReducerState | undefined,
  action: ApplicationBuildActions,
): ApplicationBuildReducerState => {
  const state = previousState ?? INITIAL_STATE;

  switch (action.type) {
    // Application builds
    case FETCH_APPLICATION_BUILDS:
      return {
        ...state,
        applicationBuilds:
          sort(action.data ?? [], {
            sortBy: state.sortBy,
            sortDirection: state.sortDirection,
            sortSearchValue: state.params?.sortSearchValue ?? state.params?.value,
          }) ?? [],
        fetchApplicationBuildsIsCompleted: true,
        fetchApplicationBuildsIsPending: false,
        fetchApplicationBuildsIsError: null,
      };
    case SORT:
      return {
        ...state,
        sortBy: action.params.sortSearchParam,
        sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
        params: action.params,
        applicationBuilds: sort(state.applicationBuilds, {
          sortBy: action.params.sortSearchParam,
          sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
          sortSearchValue: action.params.sortSearchValue ?? action.params.value,
        }),
      };
    case FETCH_APPLICATION_BUILDS_IS_PENDING:
      return {
        ...state,
        fetchApplicationBuildsIsCompleted: false,
        fetchApplicationBuildsIsPending: true,
        fetchApplicationBuildsIsError: null,
      };
    case FETCH_APPLICATION_BUILDS_IS_ERROR:
      return {
        ...state,
        fetchApplicationBuildsIsCompleted: true,
        fetchApplicationBuildsIsPending: false,
        // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        fetchApplicationBuildsIsError: parseError(action.error.message),
      };

    // Application build properties
    case CLEAN_APPLICATION_BUILD_PROPERTIES:
      return {
        ...state,
        applicationBuildProperties: [],
      };
    case FETCH_APPLICATION_BUILD_PROPERTIES:
      return {
        ...state,
        applicationBuildProperties: action.data,
        fetchApplicationBuildPropertiesIsCompleted: true,
        fetchApplicationBuildPropertiesIsPending: false,
        fetchApplicationBuildPropertiesIsError: null,
      };
    case FETCH_APPLICATION_BUILD_PROPERTIES_IS_PENDING:
      return {
        ...state,
        fetchApplicationBuildPropertiesIsCompleted: false,
        fetchApplicationBuildPropertiesIsPending: true,
        fetchApplicationBuildPropertiesIsError: null,
      };
    case FETCH_APPLICATION_BUILD_PROPERTIES_IS_ERROR:
      return {
        ...state,
        fetchApplicationBuildPropertiesIsCompleted: true,
        fetchApplicationBuildPropertiesIsPending: false,
        fetchApplicationBuildPropertiesIsError: action.error,
      };

    // Update application build properties
    case UPDATE_APPLICATION_BUILD_PROPERTIES:
      return {
        ...state,
        updateApplicationBuildPropertiesIsPending: false,
        updateApplicationBuildPropertiesIsError: null,
        updateApplicationBuildPropertiesIsCompleted: true,
      };
    case UPDATE_APPLICATION_BUILD_PROPERTIES_IS_PENDING:
      return {
        ...state,
        updateApplicationBuildPropertiesIsCompleted: false,
        updateApplicationBuildPropertiesIsPending: true,
        updateApplicationBuildPropertiesIsError: null,
      };
    case UPDATE_APPLICATION_BUILD_PROPERTIES_IS_ERROR:
      return {
        ...state,
        updateApplicationBuildPropertiesIsCompleted: true,
        updateApplicationBuildPropertiesIsPending: false,
        updateApplicationBuildPropertiesIsError: action.error,
      };

    // Create application build
    case CREATE_APPLICATION_BUILD:
      return {
        ...state,
        applicationBuilds: addNew(action.data, state.applicationBuilds) ?? [],
        createApplicationBuildIsCompleted: true,
        createApplicationBuildIsPending: false,
        createApplicationBuildIsError: null,
      };
    case CREATE_APPLICATION_BUILD_IS_PENDING:
      return {
        ...state,
        createApplicationBuildIsCompleted: false,
        createApplicationBuildIsPending: true,
        createApplicationBuildIsError: null,
      };
    case CREATE_APPLICATION_BUILD_IS_ERROR:
      return {
        ...state,
        createApplicationBuildIsCompleted: true,
        createApplicationBuildIsPending: false,
        createApplicationBuildIsError: action.error,
      };

    // Update application build
    case UPDATE_APPLICATION_BUILD: {
      const { data } = action;

      const applicationBuilds = isNil(data)
        ? state.applicationBuilds
        : state.applicationBuilds.map((build) => {
            if (build.id !== data.id) {
              return build;
            }

            return {
              ...build,
              ...data,
            };
          });

      return {
        ...state,
        applicationBuilds,
        updateApplicationBuildIsCompleted: true,
        updateApplicationBuildIsPending: false,
        updateApplicationBuildIsError: null,
      };
    }

    case UPDATE_APPLICATION_BUILD_IS_PENDING:
      return {
        ...state,
        updateApplicationBuildIsCompleted: false,
        updateApplicationBuildIsPending: true,
        updateApplicationBuildIsError: null,
      };
    case UPDATE_APPLICATION_BUILD_IS_ERROR:
      return {
        ...state,
        updateApplicationBuildIsCompleted: true,
        updateApplicationBuildIsPending: false,
        updateApplicationBuildIsError: action.error,
      };

    // Delete application build
    case DELETE_APPLICATION_BUILD: {
      const { data } = action;

      const applicationBuilds = state.applicationBuilds.filter(({ id }) => id !== data?.id);

      return {
        ...state,
        applicationBuilds,
        deleteApplicationBuildIsCompleted: true,
        deleteApplicationBuildIsPending: false,
        deleteApplicationBuildIsError: null,
      };
    }

    case DELETE_APPLICATION_BUILD_IS_PENDING:
      return {
        ...state,
        deleteApplicationBuildIsCompleted: false,
        deleteApplicationBuildIsPending: true,
        deleteApplicationBuildIsError: null,
      };
    case DELETE_APPLICATION_BUILD_IS_ERROR:
      return {
        ...state,
        deleteApplicationBuildIsCompleted: true,
        deleteApplicationBuildIsPending: false,
        deleteApplicationBuildIsError: action.error,
      };

    // Clean error
    case CLEAN_ERROR: {
      const { errorIndex, reducerProperty } = action;

      const propertyValue = state[reducerProperty];

      if (isArray(propertyValue) && propertyValue[errorIndex]) {
        return {
          ...state,
          [reducerProperty]: propertyValue.filter((_, index) => index === errorIndex),
        };
      }

      return {
        ...state,
      };
    }

    // Application build by id
    case FETCH_APPLICATION_BUILD_BY_ID:
      return {
        ...state,
        applicationBuildById: action.data,
        fetchApplicationBuildByIdIsCompleted: true,
        fetchApplicationBuildByIdIsPending: false,
        fetchApplicationBuildByIdIsError: null,
      };
    case FETCH_APPLICATION_BUILD_BY_ID_IS_PENDING:
      return {
        ...state,
        fetchApplicationBuildByIdIsCompleted: false,
        fetchApplicationBuildByIdIsPending: true,
        fetchApplicationBuildByIdIsError: null,
      };
    case FETCH_APPLICATION_BUILD_BY_ID_IS_ERROR:
      return {
        ...state,
        fetchApplicationBuildByIdIsCompleted: true,
        fetchApplicationBuildByIdIsPending: false,
        fetchApplicationBuildByIdIsError: action.error,
      };

    case 'RESET_APPLICATION_BUILD_REDUCER':
    case 'RESET_REDUCER':
      return INITIAL_STATE;
    default:
      return state;
  }
};

export default applicationBuildReducer;
